import api from "../proxy/config";
import axios from "./axiosConfig";
import { STORAGE_NAMESPACE } from "../config/newsletterEditorConfig";
import {
  parseNewsletterDocument,
  parseStorageObjectSummaries,
  parseStoragePrivateContent,
  parseStorageUploadResult,
  savedComponentSchema,
  savedLayoutBlockSchema,
  legacyLayoutSchema,
  storageObjectSummarySchema,
} from "./schemas/newsletterStorageSchemas";
import {
  stripComponentForStorage,
  stripDocumentForStorage,
  stripLayoutBlockForStorage,
} from "../components/Admin/newsletter-editor/utils/storagePayloadUtils";

const authHeaders = () => {
  const rawToken = sessionStorage.getItem("token");
  if (!rawToken) {
    throw new Error("Sessione scaduta. Effettua di nuovo l'accesso.");
  }
  try {
    return {
      headers: {
        Authorization: `Bearer ${JSON.parse(rawToken)}`,
      },
    };
  } catch {
    throw new Error("Token di autenticazione non valido.");
  }
};

const extractErrorMessage = (error, fallback) => {
  const backendError = error.response?.data;
  if (typeof backendError === "string") {
    return backendError;
  }
  if (backendError?.message) {
    return backendError.message;
  }
  return fallback;
};

export const uploadNewsletterImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await axios.post(api.storageNewsletterImageUpload, formData, authHeaders());
    return parseStorageUploadResult(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento dell'immagine"));
  }
};

export const listNewsletterImages = async () => {
  try {
    const response = await axios.post(api.storageNewsletterImageList, {}, authHeaders());
    return parseStorageObjectSummaries(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero delle immagini"));
  }
};

export const deleteNewsletterImage = async (objectKey) => {
  try {
    await axios.post(api.storageImageDelete, { objectKey }, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione dell'immagine"));
  }
};

export const listPrivateStorageObjects = async (namespace) => {
  try {
    const response = await axios.post(api.storagePrivateList, { namespace }, authHeaders());
    return parseStorageObjectSummaries(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel recupero degli oggetti"));
  }
};

export const loadPrivateStorageObject = async (namespace, objectKey) => {
  try {
    const response = await axios.post(api.storagePrivateLoad, { namespace, objectKey }, authHeaders());
    return parseStoragePrivateContent(response.data).content;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel caricamento del contenuto"));
  }
};

export const savePrivateStorageObject = async ({ namespace, objectKey, fileName, content, contentType }) => {
  try {
    const response = await axios.post(
      api.storagePrivateSave,
      { namespace, objectKey, fileName, content, contentType },
      authHeaders()
    );
    return storageObjectSummarySchema.parse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nel salvataggio"));
  }
};

export const deletePrivateStorageObject = async (namespace, objectKey) => {
  try {
    await axios.post(api.storagePrivateDelete, { namespace, objectKey }, authHeaders());
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Errore nell'eliminazione"));
  }
};

export const listNewsletterEmails = () => listPrivateStorageObjects(STORAGE_NAMESPACE.EMAILS);

export const deleteNewsletterEmail = (objectKey) =>
  deletePrivateStorageObject(STORAGE_NAMESPACE.EMAILS, objectKey);
export const listNewsletterLayouts = () => listPrivateStorageObjects(STORAGE_NAMESPACE.LAYOUTS);
export const listNewsletterComponents = () => listPrivateStorageObjects(STORAGE_NAMESPACE.COMPONENTS);

export const saveNewsletterDocument = async (document, objectKey) => {
  const payload = stripDocumentForStorage({
    ...document,
    updatedAt: new Date().toISOString(),
  });
  const summary = await savePrivateStorageObject({
    namespace: STORAGE_NAMESPACE.EMAILS,
    objectKey,
    fileName: `${payload.name || "newsletter"}.json`,
    content: JSON.stringify(payload),
    contentType: "application/json",
  });
  return {
    document: parseNewsletterDocument(payload),
    objectKey: summary.objectKey,
  };
};

export const loadNewsletterDocument = async (objectKey) => {
  const content = await loadPrivateStorageObject(STORAGE_NAMESPACE.EMAILS, objectKey);
  try {
    return parseNewsletterDocument(JSON.parse(content));
  } catch {
    throw new Error("Il documento salvato non è un JSON valido.");
  }
};

export const saveNewsletterLayout = async (grid, name, objectKey) => {
  const payload = savedLayoutBlockSchema.parse(stripLayoutBlockForStorage(name, grid));
  return savePrivateStorageObject({
    namespace: STORAGE_NAMESPACE.LAYOUTS,
    objectKey,
    fileName: `${name}.json`,
    content: JSON.stringify(payload),
    contentType: "application/json",
  });
};

export const loadNewsletterLayout = async (objectKey) => {
  const content = await loadPrivateStorageObject(STORAGE_NAMESPACE.LAYOUTS, objectKey);
  const parsed = JSON.parse(content);
  if (parsed.grid) {
    return savedLayoutBlockSchema.parse(parsed).grid;
  }
  const legacy = legacyLayoutSchema.parse(parsed);
  if (legacy.layout?.grids?.length === 1) {
    return legacy.layout.grids[0];
  }
  throw new Error("Formato layout obsoleto. Salva di nuovo il layout come blocco singolo.");
};

export const deleteNewsletterLayout = (objectKey) =>
  deletePrivateStorageObject(STORAGE_NAMESPACE.LAYOUTS, objectKey);

export const saveNewsletterComponent = async (componentPayload, objectKey) => {
  const payload = savedComponentSchema.parse(stripComponentForStorage(componentPayload));
  return savePrivateStorageObject({
    namespace: STORAGE_NAMESPACE.COMPONENTS,
    objectKey,
    fileName: `${payload.name}.json`,
    content: JSON.stringify(payload),
    contentType: "application/json",
  });
};

export const loadNewsletterComponent = async (objectKey) => {
  const content = await loadPrivateStorageObject(STORAGE_NAMESPACE.COMPONENTS, objectKey);
  return savedComponentSchema.parse(JSON.parse(content));
};

export const deleteNewsletterComponent = (objectKey) =>
  deletePrivateStorageObject(STORAGE_NAMESPACE.COMPONENTS, objectKey);

export { STORAGE_NAMESPACE };
 